import { Box, Button, FormikTextInput, Modal } from '@palmetto/palmetto-components';
import { Field, Form, Formik } from 'formik';
import { useCallback, useMemo } from 'react';
import { array, object, string } from 'yup';

import { SignatoryName } from 'types';

interface SignatoryNamesFormValues {
  signatories: Omit<SignatoryName, 'email'>[];
}

interface SignatoryNamesModalProps {
  emails: string[];
  onCancel: () => void;
  onConfirm: (signatoryNames: SignatoryName[]) => Promise<void>;
}

const requiredName = string().trim().required('Required');

const signatoryNamesSchema = object({
  signatories: array().of(object({ givenName: requiredName, familyName: requiredName })),
});

const nameDirection = { base: 'column', tablet: 'row' } as const;

const SignatoryNamesModal = ({ emails, onCancel, onConfirm }: SignatoryNamesModalProps) => {
  const initialValues = useMemo<SignatoryNamesFormValues>(
    () => ({ signatories: emails.map(() => ({ givenName: '', familyName: '' })) }),
    [emails],
  );

  const submit = useCallback(
    ({ signatories }: SignatoryNamesFormValues) =>
      onConfirm(
        signatories.map(({ givenName, familyName }, index) => ({
          email: emails[index],
          givenName: givenName.trim(),
          familyName: familyName.trim(),
        })),
      ),
    [emails, onConfirm],
  );

  return (
    <Modal ariaLabelledBy="signatoryNamesHeader" isOpen onDismiss={onCancel}>
      <Formik initialValues={initialValues} validationSchema={signatoryNamesSchema} onSubmit={submit}>
        {({ isSubmitting }) => (
          <Form noValidate>
            <Modal.Header id="signatoryNamesHeader" title="New signatories" onDismiss={onCancel} />
            <Modal.Body childGap="lg">
              <Box as="p" color="body-secondary" fontSize="sm">
                Enter a name for each new signatory so their account can be created.
              </Box>
              {emails.map((email, index) => (
                <Box key={email} childGap="xs">
                  <Box fontSize="sm" fontWeight="medium">
                    {email}
                  </Box>
                  <Box direction={nameDirection} childGap="sm">
                    <Field
                      name={`signatories.${index}.givenName`}
                      id={`newSignatory-${index}-givenName`}
                      label="First name"
                      component={FormikTextInput}
                      autoComplete="off"
                      isRequired
                      isDisabled={isSubmitting}
                    />
                    <Field
                      name={`signatories.${index}.familyName`}
                      id={`newSignatory-${index}-familyName`}
                      label="Last name"
                      component={FormikTextInput}
                      autoComplete="off"
                      isRequired
                      isDisabled={isSubmitting}
                    />
                  </Box>
                </Box>
              ))}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" tone="neutral" size="md" onClick={onCancel} isDisabled={isSubmitting}>
                Cancel
              </Button>
              <Button variant="primary" size="md" type="submit" isLoading={isSubmitting}>
                Save
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default SignatoryNamesModal;
